/**
 * Pure helpers for the Trash screen — countdown math for the 14-day
 * retention reaper and stable row keys. No React, no DOM.
 */
import type { TrashEntry } from '../../api/client.js';

export const DAY_MS = 86_400_000;

/** Stable key for selection sets / React lists (ids are only unique per kind). */
export function entryKey(e: TrashEntry): string {
  return `${e.kind}:${e.id}`;
}

export function entryTitle(e: TrashEntry): string {
  return e.title ?? (e.kind === 'sweep' ? 'Untitled session' : 'Untitled page');
}

/** Whole days until the reaper takes it — clamped at 0 for overdue rows. */
export function daysUntilPurge(purgeAt: number, now = Date.now()): number {
  return Math.max(0, Math.ceil((purgeAt - now) / DAY_MS));
}

export type PurgeTone = 'danger' | 'warn' | 'muted';

/** Red when < 3 days left, amber when < 7, quiet otherwise. */
export function purgeTone(days: number): PurgeTone {
  if (days < 3) return 'danger';
  if (days < 7) return 'warn';
  return 'muted';
}

export function purgeLabel(days: number): string {
  if (days <= 0) return 'PURGES TODAY';
  if (days === 1) return 'PURGES TOMORROW';
  return `PURGES IN ${days} DAYS`;
}

/** "DELETED 2 DAYS AGO" / "DELETED 1 WEEK AGO" — the mono meta-line voice. */
export function deletedAgo(deletedAt: number, now = Date.now()): string {
  const days = Math.floor((now - deletedAt) / DAY_MS);
  if (days <= 0) return 'DELETED TODAY';
  if (days === 1) return 'DELETED YESTERDAY';
  if (days < 7) return `DELETED ${days} DAYS AGO`;
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? 'DELETED 1 WEEK AGO' : `DELETED ${weeks} WEEKS AGO`;
}
